<template>
	<div class="team-members">
		<SgnlTable
			:columns="columns"
			:data="rows"
			:loading="membersListLoading"
			:empty-text="noDataText"
			show-column-selector
			:initial-column-visibility="{ Name: false, UserID: false }"
			:error="loadError"
			error-title="Could not load Organization Members"
			:error-message="errorMessage"
			:retrying="membersListLoading"
			@retry="refreshList"
		>
			<!-- Name -->
			<template #cell-Name="{ row }">
				<div
					class="member-name sgnl-paragraph-s-regular sgnl-text-color-default"
					:title="row.Username || '—'"
				>
					{{ row.Username || "—" }}
				</div>
			</template>

			<!-- Email -->
			<template #cell-Email="{ row }">
				<div
					class="member-email sgnl-flex sgnl-align-center"
					:title="row.Email"
				>
					<span class="member-email-text sgnl-paragraph-s-regular sgnl-text-color-secondary">{{
						row.Email
					}}</span>
					<v-chip
						v-if="row.UserID === currentUserId"
						variant="outlined"
					>
						You
					</v-chip>
				</div>
			</template>

			<!-- UserID -->
			<template #cell-UserID="{ row }">
				<div
					class="member-userid sgnl-code-default"
					:title="row.UserID || 'Pending invitation'"
					data-test="members-list-member-entry"
					:data-test-value="row.UserID"
				>
					<span v-if="row.UserID">{{ row.UserID }}</span>
					<span
						v-else
						class="sgnl-text-color-secondary sgnl-font-italic"
						>Pending invitation</span
					>
				</div>
			</template>

			<!-- Status -->
			<template #cell-Status="{ row }">
				<MemberStatusBadge :status="row.Status" />
			</template>

			<!-- Role -->
			<template #cell-Role="{ row }">
				<MemberRoleControl
					:member="row"
					:editing="!!editingRoleIdentifier && editingRoleIdentifier === row.UserID"
					:role-options="roleOptions"
					:loading="row.UserID === roleChangeLoadingUserId"
					:can-edit="!isRoleChangeDisabled(row)"
					@start-edit="startRoleEdit(row)"
					@cancel="editingRoleIdentifier = null"
					@select="onRoleSelected(row, $event)"
				/>
			</template>

			<!-- Actions -->
			<template #cell-actions="{ row }">
				<div class="actions-cell">
					<v-btn
						v-if="canRemoveMember(row)"
						icon
						variant="text"
						size="small"
						@click="handleRemoveMemberClick(row)"
						:disabled="isRemoveLoading(row)"
						:loading="isRemoveLoading(row)"
						class="delete-btn"
					>
						<TrashCan16 class="sgnl-icon-color-error" />
						<v-tooltip
							activator="parent"
							location="bottom"
							>Remove member</v-tooltip
						>
					</v-btn>
				</div>
			</template>

			<!-- Mobile card -->
			<template #card="{ row }">
				<div class="member-card__top">
					<div class="member-card__identity">
						<div class="member-card__email-row sgnl-flex sgnl-align-center sgnl-gap-8">
							<span class="member-card__primary sgnl-paragraph-s-medium sgnl-text-color-default">{{
								row.Email
							}}</span>
							<v-chip
								v-if="row.UserID === currentUserId"
								variant="outlined"
							>
								You
							</v-chip>
						</div>
						<span
							v-if="row.Username"
							class="member-card__primary sgnl-caption-regular sgnl-text-color-secondary"
							>{{ row.Username }}</span
						>
					</div>
					<MemberStatusBadge
						class="member-card__status"
						:status="row.Status"
					/>
				</div>
				<div class="member-card__bottom">
					<MemberRoleControl
						:member="row"
						:editing="!!editingRoleIdentifier && editingRoleIdentifier === row.UserID"
						:role-options="roleOptions"
						:loading="row.UserID === roleChangeLoadingUserId"
						:can-edit="!isRoleChangeDisabled(row)"
						@start-edit="startRoleEdit(row)"
						@cancel="editingRoleIdentifier = null"
						@select="onRoleSelected(row, $event)"
					/>
					<v-btn
						v-if="canRemoveMember(row)"
						icon
						variant="text"
						size="small"
						class="delete-btn"
						@click="handleRemoveMemberClick(row)"
						:disabled="isRemoveLoading(row)"
						:loading="isRemoveLoading(row)"
					>
						<TrashCan16 class="sgnl-icon-color-error" />
						<v-tooltip
							activator="parent"
							location="bottom"
							>Remove member</v-tooltip
						>
					</v-btn>
				</div>
			</template>
		</SgnlTable>

		<!-- Role Change Confirmation Dialog -->
		<v-dialog
			v-model="roleChangeDialog"
			:width="$vuetify.display.mobile ? '95%' : '500px'"
			@click:outside="cancelRoleChange"
			@keydown.esc="cancelRoleChange"
		>
			<v-card class="sgnl-pb-0">
				<v-card-title class="dialog-header sgnl-subtitle-medium"
					>Confirm Role Change
					<v-spacer />
					<v-btn
						icon
						variant="text"
						size="small"
						@click="cancelRoleChange"
					>
						<Close16 />
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="dialog-body sgnl-paragraph-s-regular">
					<div v-if="pendingRoleChange">
						Are you sure you want to change
						<strong>{{ pendingRoleChange.member.Username || pendingRoleChange.member.Email }}</strong
						>'s role from <strong>{{ pendingRoleChange.oldRole }}</strong> to
						<strong>{{ pendingRoleChange.newRole }}</strong
						>?
					</div>
					<v-alert
						v-if="roleChangeError"
						type="error"
						density="compact"
					>
						{{ roleChangeError }}
					</v-alert>
				</v-card-text>
				<v-divider />
				<v-card-actions class="dialog-footer">
					<v-btn
						variant="outlined"
						@click="cancelRoleChange"
						:disabled="roleChangeLoading"
					>
						Cancel
					</v-btn>
					<v-btn
						variant="flat"
						color="primary"
						@click="confirmRoleChange"
						:loading="roleChangeLoading"
					>
						Confirm
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Remove Member Confirmation Dialog -->
		<v-dialog
			v-model="removeMemberDialog"
			:width="$vuetify.display.mobile ? '95%' : '500px'"
			@click:outside="cancelRemoveMember"
			@keydown.esc="cancelRemoveMember"
		>
			<v-card class="sgnl-pb-0">
				<v-card-title class="dialog-header sgnl-subtitle-medium"
					>Remove Member
					<v-spacer />
					<v-btn
						icon
						variant="text"
						size="small"
						@click="cancelRemoveMember"
					>
						<Close16 />
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="dialog-body sgnl-paragraph-s-regular">
					<div
						v-if="pendingRemoveMember"
						class="sgnl-flex sgnl-flex-column sgnl-gap-12"
					>
						<div>
							Are you sure you want to remove
							<strong>{{ pendingRemoveMember.Username || pendingRemoveMember.Email }}</strong> from the
							organization?
						</div>
						<AlertMessage type="warning">
							<div class="sgnl-flex sgnl-flex-column sgnl-gap-8">
								<div>
									This action cannot be undone. The member will lose access to all organization
									resources.
								</div>
								<div v-if="pendingRemoveMember.UserID === currentUserId">
									You will be signed out immediately after removal.
								</div>
							</div>
						</AlertMessage>
					</div>
					<AlertMessage
						v-if="removeMemberError"
						type="error"
					>
						{{ removeMemberError }}
					</AlertMessage>
				</v-card-text>
				<v-divider />
				<v-card-actions class="dialog-footer">
					<v-btn
						variant="outlined"
						@click="cancelRemoveMember"
						:disabled="removeMemberLoading"
					>
						Cancel
					</v-btn>
					<v-btn
						variant="flat"
						color="error"
						@click="confirmRemoveMember"
						:loading="removeMemberLoading"
					>
						Remove
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Success Snackbar -->
		<v-snackbar
			v-model="successSnackbar"
			:timeout="3000"
			class="snackbar-success"
		>
			<div class="snackbar-icon-container snackbar-success">
				<CheckmarkFilled16 />
			</div>
			{{ successMessage }}
		</v-snackbar>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";
import TrashCan16 from "@carbon/icons-vue/es/trash-can/16";

// Types
import { AWSAuthenticatedUserObject } from "@/types/user";
import { OrganizationUserFormatted, OrganizationUser, OrganizationRole } from "@/types/api/organizations";

// Libraries
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import { formatCreatedAt } from "@/js/dateUtils";

// Utilities
import * as Sentry from "@sentry/vue";
import * as util from "@/js/utilities";

// Stores
import { mapStores } from "pinia";
import { useUserStore } from "@/stores/user";
import axios from "axios";
import { signOut } from "aws-amplify/auth";
import { isAxiosTimeoutError, monitoringCaptureError } from "@/plugins/monitoring";
import AlertMessage from "@/components/Common/AlertMessage.vue";
import SgnlTable from "@/components/Common/Table.vue";
import MemberRoleControl from "@/components/Settings/Organization/MemberRoleControl.vue";
import MemberStatusBadge from "@/components/Settings/Organization/MemberStatusBadge.vue";
import { type ColumnDef } from "@tanstack/vue-table";

function formatMemberDates(member: OrganizationUser): OrganizationUserFormatted {
	return {
		...member,
		FormattedJoinedAt: member.JoinedAt ? formatCreatedAt(new Date(member.JoinedAt).getTime() / 1000) : undefined,
		FormattedCreatedAt: member.CreatedAt ? formatCreatedAt(new Date(member.CreatedAt).getTime() / 1000) : undefined,
	};
}

// Name and UserID are hideable (and hidden by default); the rest always show.
const MEMBER_COLUMNS: ColumnDef<OrganizationUserFormatted>[] = [
	{ id: "Name", header: "Name", accessorFn: (member) => member.Username ?? "" },
	{ accessorKey: "Email", header: "Email Address", enableHiding: false },
	{ accessorKey: "UserID", header: "UserID", enableSorting: false },
	{ id: "Status", header: "Status", accessorFn: (member) => member.Status, enableHiding: false },
	{ accessorKey: "Role", header: "Role", enableHiding: false },
	{ id: "actions", header: "Actions", enableSorting: false, enableHiding: false },
];

type ComponentData = {
	authenticatedUser: undefined | null | AWSAuthenticatedUserObject;
	membersListData: OrganizationUserFormatted[];
	currentUserId: string | null;
	membersListLoading: boolean;
	noDataText: string;
	loadError: boolean;
	errorMessage: string;
	roleOptions: OrganizationRole[];
	organizationId: string | null;
	roleChangeDialog: boolean;
	roleChangeLoading: boolean;
	roleChangeLoadingUserId: string | null;
	editingRoleIdentifier: string | null;
	roleChangeError: string;
	pendingRoleChange: {
		member: OrganizationUserFormatted;
		oldRole: OrganizationRole;
		newRole: OrganizationRole;
	} | null;
	successSnackbar: boolean;
	successMessage: string;
	ownerCount: number;
	currentUserRole: OrganizationRole | null;
	removeMemberDialog: boolean;
	removeMemberLoading: boolean;
	removeLoadingIdentifier: string | null;
	removeMemberError: string;
	pendingRemoveMember: OrganizationUserFormatted | null;
};

export default defineComponent({
	name: "ListTeamMembers",
	props: {},
	components: {
		Close16,
		CheckmarkFilled16,
		TrashCan16,
		AlertMessage,
		SgnlTable,
		MemberRoleControl,
		MemberStatusBadge,
	},
	emits: {
		roleUpdated: (_role: OrganizationRole | null) => true,
	},
	data: (): ComponentData => ({
		authenticatedUser: undefined,
		membersListData: [],
		currentUserId: null,
		membersListLoading: false,
		noDataText: "No team members found.",
		loadError: false,
		errorMessage: "",
		roleOptions: ["Owner", "Member"],
		organizationId: null,
		roleChangeDialog: false,
		roleChangeLoading: false,
		roleChangeLoadingUserId: null,
		editingRoleIdentifier: null,
		roleChangeError: "",
		pendingRoleChange: null,
		successSnackbar: false,
		successMessage: "",
		ownerCount: 0,
		currentUserRole: null,
		removeMemberDialog: false,
		removeMemberLoading: false,
		removeLoadingIdentifier: null,
		removeMemberError: "",
		pendingRemoveMember: null,
	}),
	computed: {
		...mapStores(useUserStore),
		columns(): ColumnDef<OrganizationUserFormatted>[] {
			return MEMBER_COLUMNS;
		},
		// Sort to put the current user first; column sorting takes over once a header is clicked.
		rows(): OrganizationUserFormatted[] {
			return [...this.membersListData].sort((a, b) => {
				const aIsCurrentUser = a.UserID === this.currentUserId;
				const bIsCurrentUser = b.UserID === this.currentUserId;
				if (aIsCurrentUser && !bIsCurrentUser) return -1;
				if (!aIsCurrentUser && bIsCurrentUser) return 1;
				return 0;
			});
		},
	},
	methods: {
		async refreshList() {
			this.membersListLoading = true;
			this.loadError = false;
			try {
				if (!this.organizationId) {
					this.noDataText = "No organization found for this user.";
					return;
				}

				const membersResponse = await sdk.organizations.listUsers(this.organizationId);

				// Combine active users and pending invitations
				const allMembers = [...membersResponse.Users, ...membersResponse.Pending];

				this.membersListData = allMembers.map(formatMemberDates);

				// The org API returns the signed-in user's email as their Username; show their profile name instead.
				const ownProfileName = this.userStore.currentUser?.name;
				if (ownProfileName) {
					const ownEntry = this.membersListData.find((m) => m.UserID === this.currentUserId);
					if (ownEntry) {
						ownEntry.Username = ownProfileName;
					}
				}

				// Calculate owner count for business logic
				this.ownerCount = this.membersListData.filter(
					(m) => m.Role === "Owner" && m.Status === "Active",
				).length;

				// Determine current user's role
				const currentUserMember = this.membersListData.find(
					(m) => m.UserID === this.currentUserId && m.Status === "Active",
				);
				this.currentUserRole = currentUserMember?.Role || null;
			} catch (error) {
				let severityLevel: Sentry.SeverityLevel = "error";
				if (error instanceof SdkError) {
					switch (error.code) {
						case ERROR_CODES.API_SERVER_ERROR:
							severityLevel = "fatal";
							this.errorMessage = "Our system encountered a server error retrieving the team members.";
							break;
						case ERROR_CODES.API_UNAUTHORIZED:
							this.errorMessage =
								"Our system encountered an authorization error while retrieving the team members." +
								" Signing out and back in will ensure you are properly authorized.";
							break;
						case ERROR_CODES.API_NOT_FOUND:
							severityLevel = "fatal";
							this.errorMessage =
								"Our system encountered an error while locating your team members." +
								" Our team will receive a high priority notification for this issue.";
							break;
						default:
							this.errorMessage =
								"Our system encountered an unexpected error retrieving the team members.";
							break;
					}
				} else if (axios.isAxiosError(error)) {
					// SDK forwards raw axios errors only when there's no response (network failure).
					this.errorMessage =
						"Failed to load team members. Make sure you have an active internet connection.";
					if (isAxiosTimeoutError(error)) {
						severityLevel = "info";
					}
				} else {
					this.errorMessage = "The network request could not be made due to an unexpected error.";
				}
				this.loadError = true;
				Sentry.captureException(error, {
					level: severityLevel,
					extra: { error: error, userMessage: this.errorMessage },
					tags: { service: "list-team-members" },
				});
			} finally {
				this.membersListLoading = false;
				// Signal load completion (role resolved, or null on error) so the parent
				// can swap the invite-button placeholder for the real button or hide it.
				this.$emit("roleUpdated", this.currentUserRole);
			}
		},
		startRoleEdit(member: OrganizationUserFormatted) {
			this.editingRoleIdentifier = member.UserID ?? null;
		},
		onRoleSelected(member: OrganizationUserFormatted, newRole: OrganizationRole) {
			this.editingRoleIdentifier = null;
			this.handleRoleChangeClick(member, newRole);
		},
		handleRoleChangeClick(member: OrganizationUserFormatted, newRole: OrganizationRole) {
			const oldRole = member.Role;

			// Don't show dialog if role didn't actually change
			if (oldRole === newRole) {
				return;
			}

			// Set up pending change
			this.pendingRoleChange = {
				member: member,
				oldRole: oldRole,
				newRole: newRole,
			};

			this.roleChangeError = "";
			this.roleChangeDialog = true;
		},

		async confirmRoleChange() {
			if (!this.pendingRoleChange || !this.organizationId) {
				return;
			}

			const { member, newRole } = this.pendingRoleChange;

			if (!member.UserID) {
				this.roleChangeError = "Cannot change role for pending users.";
				return;
			}

			const isChangingSelfRole = member.UserID === this.currentUserId;

			this.roleChangeLoading = true;
			this.roleChangeLoadingUserId = member.UserID;
			this.roleChangeError = "";

			try {
				await sdk.organizations.updateUserRole(this.organizationId, member.UserID, {
					Role: newRole,
				});

				// Update the role in the UI
				member.Role = newRole;

				// If changing own role, update currentUserRole to trigger reactive UI updates
				if (isChangingSelfRole) {
					this.currentUserRole = newRole;
					// Emit role to parent component
					this.$emit("roleUpdated", this.currentUserRole);
				}

				// Close dialog
				this.roleChangeDialog = false;
				this.pendingRoleChange = null;

				// Show success message
				this.successMessage = `Successfully changed role to ${newRole}`;
				this.successSnackbar = true;
				this.$posthog?.capture("team_member_role_changed", { new_role: newRole });

				// Refresh the list to get updated data
				await this.refreshList();
			} catch (error) {
				let errorMessage = "Failed to update role. Please try again.";

				if (error instanceof SdkError) {
					switch (error.code) {
						case ERROR_CODES.API_BAD_REQUEST:
							// "at least one owner" is the server's own message; surface it verbatim
							// with a friendlier wrapper when we recognise it.
							if (error.message?.toLowerCase().includes("at least one owner")) {
								errorMessage = "Cannot change role. Organization must have at least one owner.";
							} else {
								errorMessage = error.message || "Invalid request.";
							}
							break;
						case ERROR_CODES.API_FORBIDDEN:
							errorMessage = "You don't have permission to change roles.";
							break;
						case ERROR_CODES.API_NOT_FOUND:
							errorMessage = "User not found in organization.";
							break;
						case ERROR_CODES.API_SERVER_ERROR:
							errorMessage = "Server error. Please contact support.";
							break;
					}
				} else if (axios.isAxiosError(error)) {
					errorMessage = "Failed to update the role. Make sure you have an active internet connection.";
				}

				this.roleChangeError = errorMessage;

				Sentry.captureException(error, {
					level: "error",
					extra: { userMessage: errorMessage },
					tags: { service: "update-organization-role" },
				});
			} finally {
				this.roleChangeLoading = false;
				this.roleChangeLoadingUserId = null;
			}
		},

		cancelRoleChange() {
			this.roleChangeDialog = false;
			this.pendingRoleChange = null;
			this.roleChangeError = "";
		},

		isRoleChangeDisabled(member: OrganizationUserFormatted): boolean {
			// Members can only view, not modify
			if (this.currentUserRole === "Member") {
				return true;
			}

			// Disable for pending users (they don't have UserID yet)
			if (!member.UserID || member.Status === "Pending") {
				return true;
			}

			// Prevent user from demoting themselves if they're the last owner
			if (member.UserID === this.currentUserId && member.Role === "Owner" && this.ownerCount === 1) {
				return true;
			}

			return false;
		},

		canRemoveMember(member: OrganizationUserFormatted): boolean {
			// Can remove self (only applies to active members with UserID), but not if last owner
			if (member.UserID && member.UserID === this.currentUserId) {
				// Prevent last owner from removing themselves
				if (member.Role === "Owner" && this.ownerCount === 1) {
					return false;
				}
				return true;
			}

			// Only owners can remove members
			if (this.currentUserRole !== "Owner") {
				return false;
			}

			// Can remove pending invitations or active members
			return true;
		},

		isRemoveLoading(member: OrganizationUserFormatted): boolean {
			if (!this.removeLoadingIdentifier) {
				return false;
			}
			// For active users, compare UserID; for pending invites, compare encoded email
			const identifier = member.UserID || encodeURIComponent(member.Email);
			return this.removeLoadingIdentifier === identifier;
		},

		handleRemoveMemberClick(member: OrganizationUserFormatted) {
			this.pendingRemoveMember = member;
			this.removeMemberError = "";
			this.removeMemberDialog = true;
		},

		async confirmRemoveMember() {
			if (!this.pendingRemoveMember || !this.organizationId) {
				return;
			}

			const member = this.pendingRemoveMember;
			const isRemovingSelf = member.UserID === this.currentUserId;

			// For active users, use UserID; for pending invites, use encoded email
			const identifier = member.UserID || encodeURIComponent(member.Email);

			this.removeMemberLoading = true;
			this.removeLoadingIdentifier = identifier;
			this.removeMemberError = "";

			try {
				await sdk.organizations.removeUser(this.organizationId, identifier);

				// Close dialog
				this.removeMemberDialog = false;
				this.pendingRemoveMember = null;

				// Show success message
				const action = member.Status === "Pending" ? "deleted invitation for" : "removed";
				this.successMessage = `Successfully ${action} ${member.Username || member.Email}`;
				this.successSnackbar = true;
				this.$posthog?.capture("team_member_removed");

				// If removing self, sign out to clear session and org data
				if (isRemovingSelf) {
					await this.awsSignOut();
				}

				// Refresh the list to get updated data
				await this.refreshList();
			} catch (error) {
				let errorMessage = "Failed to remove member. Please try again.";

				if (error instanceof SdkError) {
					switch (error.code) {
						case ERROR_CODES.API_BAD_REQUEST:
							errorMessage = error.message || "Invalid request.";
							break;
						case ERROR_CODES.API_FORBIDDEN:
							errorMessage = "You don't have permission to remove members.";
							break;
						case ERROR_CODES.API_NOT_FOUND:
							errorMessage = "Member not found in organization.";
							break;
						case ERROR_CODES.API_SERVER_ERROR:
							errorMessage = "Server error. Please contact support.";
							break;
					}
				} else if (axios.isAxiosError(error)) {
					errorMessage = "Failed to remove the member. Make sure you have an active internet connection.";
				}

				this.removeMemberError = errorMessage;

				Sentry.captureException(error, {
					level: "error",
					extra: { userMessage: errorMessage },
					tags: { service: "remove-organization-member" },
				});
			} finally {
				this.removeMemberLoading = false;
				this.removeLoadingIdentifier = null;
			}
		},
		async awsSignOut() {
			try {
				await signOut();
				util.clearLocalState();
				window.location.href = "/login";
			} catch (error) {
				monitoringCaptureError(error, "User sign out");
				util.clearLocalState();
				window.location.href = "/login";
			}
		},
		cancelRemoveMember() {
			this.removeMemberDialog = false;
			this.pendingRemoveMember = null;
			this.removeMemberError = "";
		},
	},
	async created() {
		const authUser = await this.userStore.getCurrentAuthenticatedUserObject();
		this.authenticatedUser = authUser;

		this.currentUserId = await this.userStore.getCurrentUserID();
		this.organizationId = await this.userStore.getCurrentUserOrganizationID();

		if (this.organizationId) {
			await this.refreshList();
		}
	},
});
</script>

<style lang="css" scoped>
/* Column widths target the table cells rendered inside the Table wrapper. */
:deep(.col-Name) {
	width: 15%;
}

:deep(.col-Email) {
	width: 30%;
}

:deep(.col-UserID) {
	width: 20%;
}

:deep(.col-Status) {
	width: 12%;
}

:deep(.col-Role) {
	width: 12%;
}

:deep(.col-actions) {
	width: 6%;
}

.member-name {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.member-userid {
	max-width: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.member-userid > span {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	min-width: 0;
	color: var(--sgnl-default-secondary-text);
}

.member-email {
	max-width: 100%;
	min-width: 0;
	flex-wrap: nowrap;
	gap: var(--sgnl-space-8);
}

.member-email-text {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
}

.member-email :deep(.v-chip) {
	flex-shrink: 0;
}

.actions-cell {
	display: flex;
	align-items: center;
}

/* === Mobile card sub-layout (the Table wrapper provides the card container) === */
.member-card__top {
	display: flex;
	align-items: flex-start;
	gap: var(--sgnl-space-8);
}

.member-card__identity {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-4);
}

.member-card__email-row {
	min-width: 0;
}

.member-card__email-row .member-card__primary {
	flex: 1;
}

.member-card__email-row :deep(.v-chip) {
	flex-shrink: 0;
}

.member-card__primary {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
}

.member-card__status {
	flex-shrink: 0;
}

.member-card__bottom {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-8);
}
</style>
